// Theme-driven checkbox — the boolean control primitive magviz owns outright.
// Pure DOM; lifts into webpic's @webpic/ui/controls. Emits the `.mz-ckbv_l/_i/_w`
// + SVG-checkmark DOM that the slice label-cell hijack reuses, so the theme
// styling covers both.

import type { Disposer } from '../../types';

export interface CheckboxHandle {
    /** Root `<label.mz-ckbv_l>` — append into a value cell or the slice label cell. */
    el: HTMLLabelElement;
    input: HTMLInputElement;
    /** Reflect an external value without firing onChange. */
    set(checked: boolean): void;
    setDisabled(disabled: boolean): void;
    dispose: Disposer;
}

const SVG_NS = 'http://www.w3.org/2000/svg';

export function createCheckbox(opts: { checked?: boolean; ariaLabel?: string; onChange: (checked: boolean) => void }): CheckboxHandle {
    const label = document.createElement('label');
    label.className = 'mz-ckbv_l';
    if (opts.ariaLabel) label.setAttribute('aria-label', opts.ariaLabel);

    // Sibling order matters: the `.mz-ckbv_i:checked + .mz-ckbv_w svg` rule
    // toggles the checkmark, so the input must precede the box.
    const input = document.createElement('input');
    input.type = 'checkbox';
    input.className = 'mz-ckbv_i';
    input.checked = !!opts.checked;

    const box = document.createElement('div');
    box.className = 'mz-ckbv_w';
    const svg = document.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('viewBox', '0 0 16 16');
    const path = document.createElementNS(SVG_NS, 'path');
    path.setAttribute('d', 'M2 8l4 4l8 -8');
    svg.appendChild(path);
    box.appendChild(svg);

    label.append(input, box);

    const onChange = (): void => opts.onChange(input.checked);
    input.addEventListener('change', onChange);

    return {
        el: label,
        input,
        set(checked) { input.checked = checked; },
        setDisabled(disabled) { input.disabled = disabled; },
        dispose() { input.removeEventListener('change', onChange); label.remove(); },
    };
}
